import { LitElement, html, css, utils } from '@hh/global.js'

const { debounce } = utils

class HHHeaderSearchDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    isMobileReady: {
      type: Boolean,
      reflect: true,
      attribute: 'is-mobile-ready'
    },
    filled: {
      type: Boolean,
      reflect: true
    },
    filledResult: {
      type: Boolean,
      reflect: true,
      attribute: 'filled-result'
    },
    mod: {
      type: String,
      attribute: 'mod'
    }
  }

  constructor() {
    super()
    this.open = false
    this.isMobileReady = false
    this.filled = false
    this.mod = this.getAttribute('data-mod')

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event)
    }, 300)

    this.searchInput.addEventListener(
      'input',
      this.debouncedOnChange.bind(this)
    )

    this.searchClear.addEventListener('click', this.onClear.bind(this))

    if (this.mod == 'mobile') {
      this.searchInput.addEventListener('focusin', () => {
        if (this.searchInput.getAttribute('data-auto-focused') == 'true') {
          this.searchInput.removeAttribute('data-auto-focused')
          return false
        }
        this.isMobileReady = true
      })

      window.addEventListener('modalClosed', this.onClose.bind(this))
      this.searchInputClose.addEventListener('click', this.onClose.bind(this))
    }

    this.searchDrawerCloseEls.forEach(searchDrawerClose => searchDrawerClose.addEventListener('click', this.closeSearchDrawer.bind(this)))
  }

  connectedCallback() {
    super.connectedCallback()
    this.overlay.addEventListener('click', this.closeSearchDrawer.bind(this))
    window.addEventListener('triggerHeaderSearch', this.openSearchDrawer.bind(this))
    window.addEventListener('triggerAutoComplete', this.onAutoComplete.bind(this))
    this._handleKeyDown = this._handleKeyDown.bind(this)
    window.addEventListener('keydown', this._handleKeyDown)
  }

  _handleKeyDown(e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && this.open) {
      this.closeSearchDrawer()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.overlay.removeEventListener('click', this.closeSearchDrawer)
    window.removeEventListener('triggerHeaderSearch', this.openSearchDrawer)
    window.removeEventListener('triggerAutoComplete', this.onAutoComplete)
    window.removeEventListener('keydown', this._handleKeyDown)
  }

  firstUpdated() {
  }

  onClose() {
    this.isMobileReady = false
    this.filledResult = false
    this.filled = false
    this.searchInput.value = ''
  }

  onChange() {
    const searchTerm = this.searchInput.value.trim()

    if (!searchTerm.length) {
      this.onClose()
      return;
    }

    this.filled = true
    this.isMobileReady = true
    this.getSearchResults(searchTerm)
  }

  getSearchResults(searchTerm) {
    fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status)
          // this.close()
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML
        this.searchResults.innerHTML = resultsMarkup
        this.filledResult = true
      })
      .catch((error) => {
        throw error
      });
  }

  onAutoComplete(e) {
    if (e.detail?.key) {
      this.searchInput.value = e.detail?.key
      this.searchInput.dispatchEvent(new Event('change', { bubbles: true }))
      this.onChange()
    }
  }

  onClear(e) {
    this.searchInput.value = ''
    this.searchInput.dispatchEvent(new Event('change', { bubbles: true }))
    this.onChange()
  }

  // Open and close methods
  openSearchDrawer(e) {
    this.open = true
    /*document.querySelector('.header-search__trigger')?.setAttribute('aria-expanded', 'true')*/
    setTimeout(() => {
      this.querySelector('[slot="input"]')?.focus()
      this.querySelector('[slot="close-button"]')?.classList.add('focus-visible')
    }, 100)
  }

  closeSearchDrawer(event) {
    // Without this, the click bubbles up to document.body - and if the
    // mobile menu drawer is open underneath (search can be triggered from
    // its own cloned top bar), hh-details-modal's own "click anywhere
    // outside me" listener treats this as an outside click and closes the
    // mobile menu too, since this drawer's markup lives outside the mobile
    // menu's DOM branch and was never marked as an "intercepted" click.
    event?.stopPropagation()
    this.open = false
    this.currentMenu = ''
    /*document.querySelector('.header-search__trigger')?.setAttribute('aria-expanded', 'false')*/

    // Reset the search state on every close, not just the mobile
    // auto-focus/modalClosed path (onClose) - without this, closing via the
    // close button or the mobile "Cancel" button (both wired to this
    // method, not to onClose) left `filled`/`filledResult` stuck true and
    // the previous predictive-search results markup still in the DOM. On
    // reopening, that stale results block rendered above the Cancel button
    // instead of the short recommended-searches list, pushing Cancel button
    // further down the drawer - on a mobile viewport that could push it
    // below the fold entirely, making it look unresponsive/hidden.
    this.onClose()
  }

  updated(changedProperties) {
    if (changedProperties.has('open') && typeof changedProperties.get('open') !== 'undefined') {
      this.toggleOverlay()
      this.toggleBodyScroll()
    }
  }

  toggleOverlay() {
    this.overlay.classList[this.open ? 'add' : 'remove']('active')
  }

  // Locks scrolling via overflow:hidden on <html> rather than the old
  // "body.no-scroll" (position:fixed) trick - see the identical fix and
  // its rationale in component-mega-menu-drawer.js. That old approach
  // detaches the page from its scroll position entirely (position:fixed
  // with no top offset resets it, and document.body.scrollTo() here was
  // always a no-op since body isn't the scrolling container), which left
  // window.pageYOffset unreliable right after closing - opening this
  // drawer from inside the already scroll-locked mobile menu and then
  // closing it could leave things in a state where the close button
  // stopped responding on a later open. overflow:hidden never touches
  // scrollY at all, so there's nothing to desync.
  toggleBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    if (this.open) {
      document.documentElement.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        document.documentElement.style.paddingRight = `${scrollbarWidth}px`
      }
    } else {
      document.documentElement.style.overflow = ''
      document.documentElement.style.paddingRight = ''
      setTimeout(() => {
        this.stickyHeader.externalRevealHeader()
      }, 0)
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <hh-focus-trap active="${this.open}">
          <slot name="close-button"></slot>
          <slot name="content"></slot>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }
  `

  // Getters
  get windowHeight() {
    return window.innerHeight
  }

  get totalHeaderHeight() {
    return this.header.offsetHeight + this.announcementBar.offsetHeight
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get stickyHeader() {
    return document.querySelector('hh-sticky-header')
  }

  get overlay() {
    return document.querySelector('.js-header-search-overlay') ?? null
  }

  get searchInput() {
    return this.querySelector('.js-search-input input') ?? null
  }

  get searchResults() {
    return this.querySelector('.js-search-results') ?? null
  }

  get searchDrawerCloseEls() {
    return this.querySelectorAll('.js-search-drawer-close')
  }

  get searchInputClose() {
    return this.querySelector('.js-search-input-close') ?? null
  }

  get searchClear() {
    return this.querySelector('.js-search-clear') ?? null
  }
}

customElements.define('hh-header-search-drawer', HHHeaderSearchDrawer)
